import React from "react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandGroup,
  CommandEmpty,
} from "@src/lib/ui/Command";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@src/lib/ui/HoverCard";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import { ChatThumbnail } from "../ChatAttachment/ChatThumbnail";
import {
  ChatAttachmentLibraryItemFragment,
  ChatAttachmentMediaFragment,
} from "@src/features/assistantChat/aiChat.generated";
import { route } from "nextjs-routes";
import { useLibraryItemTypeCopy } from "@src/features/contentLibrary/ContentAvatar";
import ChevronRightIcon from "@src/lib/icons/18px/chevron-right";
import ArrowUploadIcon from "@src/lib/icons/18px/arrow-upload";
import ArrowUpRightIcon from "@src/lib/icons/18px/arrow-up-right";

export interface SelectableItem {
  libraryItem: ChatAttachmentLibraryItemFragment;
}

export type FilterType = "Course" | "Resource" | null;

export interface ChatAttachMenuContentProps {
  selectableItems: SelectableItem[];
  selectedFilter: FilterType;
  setSelectedFilter: (filter: FilterType) => void;
  onSelectItem: (item: SelectableItem) => void;
  onUploadClick?: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  isLoading?: boolean;
}

export const AttachmentHoverCard: React.FC<{
  libraryItemFragment?: ChatAttachmentLibraryItemFragment | null;
  mediaFragment?: ChatAttachmentMediaFragment | null;
  children: React.ReactNode;
  hoverCardContentProps?: Partial<
    React.ComponentProps<typeof HoverCardContent>
  >;
}> = ({
  libraryItemFragment,
  mediaFragment,
  children,
  hoverCardContentProps,
}) => {
  const libraryItemTypeCopy = useLibraryItemTypeCopy();
  const attachmentInfo = React.useMemo(() => {
    if (libraryItemFragment) {
      return {
        name: libraryItemFragment.name?.en || "No name available",
        type: libraryItemTypeCopy[libraryItemFragment.type],
        isLibraryItem: true,
      };
    }

    if (mediaFragment) {
      const mediaUrl = mediaFragment.mediaUrls?.en || "";
      const filename = mediaUrl.split("/").pop() || "";
      const decodedFilename = decodeURIComponent(filename);

      return {
        name: decodedFilename || "Media file",
        type: "Media Upload",
        isLibraryItem: false,
        mediaUrl,
      };
    }

    return {
      name: "Unknown attachment",
      type: "Unknown",
      isLibraryItem: false,
    };
  }, [libraryItemFragment, mediaFragment]);

  const handleOnClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (attachmentInfo.isLibraryItem && libraryItemFragment?.id) {
      window.open(
        route({
          pathname: "/library/library-item/[id]",
          query: { id: libraryItemFragment.id },
        }),
        "_blank",
      );
    } else if (attachmentInfo.mediaUrl) {
      window.open(attachmentInfo.mediaUrl, "_blank");
    }
  };

  return (
    <HoverCard>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side="left"
        className="p-2 rounded-lg"
        {...hoverCardContentProps}
      >
        <div className="flex items-center gap-2">
          <ChatThumbnail
            libraryItemFragment={libraryItemFragment}
            mediaFragment={mediaFragment}
            className="flex-shrink-0"
            size={32}
          />
          <div className="flex flex-col flex-1">
            <Text variant="fine-print-title" ellipsis>
              {attachmentInfo.name}
            </Text>
            <Text
              variant="fine-print-description"
              className="text-muted-foreground"
            >
              {attachmentInfo.type}
            </Text>
          </div>
          <Button size="icon" variant="ghost" onClick={handleOnClick}>
            <ArrowUpRightIcon className="size-4" />
          </Button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const LibraryItemRow: React.FC<{
  item: SelectableItem;
  onSelect: (item: SelectableItem) => void;
}> = ({ item, onSelect }) => {
  const { libraryItem } = item;
  const libraryItemTypeCopy = useLibraryItemTypeCopy();

  const contentType = libraryItemTypeCopy[libraryItem.type];

  return (
    <AttachmentHoverCard libraryItemFragment={item.libraryItem}>
      <CommandItem
        value={libraryItem.id}
        onSelect={() => onSelect(item)}
        className="flex items-center gap-1.5 px-1.5 py-1.5 cursor-pointer"
      >
        <ChatThumbnail
          libraryItemFragment={libraryItem}
          size={20}
          className="flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <Text variant="compact-description" className="line-clamp-1">
            {libraryItem.name?.en || "Untitled"}
          </Text>
        </div>
        <Text
          variant="fine-print-description"
          className="text-muted-foreground flex-shrink-0"
        >
          {contentType}
        </Text>
      </CommandItem>
    </AttachmentHoverCard>
  );
};

export const ChatAttachMenuContent: React.FC<ChatAttachMenuContentProps> = ({
  selectableItems,
  selectedFilter,
  setSelectedFilter,
  onSelectItem,
  onUploadClick,
  searchValue,
  onSearchChange,
  isLoading = false,
}) => {
  const showMainMenu = !selectedFilter && !searchValue;

  return (
    <Command className="w-[400px]" shouldFilter={false}>
      <CommandInput
        placeholder="Search library items..."
        value={searchValue}
        onValueChange={onSearchChange}
        className="border-0"
      />

      <CommandList className="max-h-[400px] overflow-y-auto">
        {showMainMenu && (
          <CommandGroup>
            <CommandItem
              onSelect={() => setSelectedFilter("Course")}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer"
            >
              <Text variant="compact-description">Courses</Text>
              <ChevronRightIcon className="ml-auto text-muted-foreground size-4" />
            </CommandItem>

            <CommandItem
              onSelect={() => setSelectedFilter("Resource")}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer"
            >
              <Text variant="compact-description">Resources</Text>
              <ChevronRightIcon className="ml-auto text-muted-foreground size-4" />
            </CommandItem>

            {onUploadClick && (
              <>
                <CommandItem
                  onSelect={onUploadClick}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                >
                  <ArrowUploadIcon className="size-4" />
                  <Text variant="compact-description">Upload files</Text>
                </CommandItem>
              </>
            )}
          </CommandGroup>
        )}

        {!showMainMenu && (
          <CommandGroup>
            {selectableItems.length === 0 ? (
              <CommandEmpty>No items found.</CommandEmpty>
            ) : (
              selectableItems.map((item) => {
                console.log("item", item);
                return (
                  <LibraryItemRow
                    key={item.libraryItem.id}
                    item={item}
                    onSelect={onSelectItem}
                  />
                );
              })
            )}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
};
